import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Input } from "../components/ui/input";
import { Ticket } from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Plane,
  Shield,
  Award,
  Users,
  Clock,
  MapPin,
  Phone,
  Mail,
  Star,
  CheckCircle2,
  MessageCircle,
  Navigation,
  HeartHandshake,
  Sparkles,
  Menu,
  X,
  Calendar,
  Info,
  Crown,
  Briefcase,
  Vote,
  Heart,
  Flower2,
  Landmark,
  ArrowRight,
} from "lucide-react";
import { mockData } from "../utils/mock";
import hangar1 from "../assets/hangar1.jpeg";
import hangar2 from "../assets/hangar2.jpeg";
import hangar3 from "../assets/hangar3.jpeg";
import teamPhoto from "../assets/highkraft_team_jakkur.jpeg";
// Services data for the "Our Services" section
const servicesData = [
  {
    id: "vip-charter",
    name: "VIP Charter",
    description:
      "Luxury helicopter charter for VIP guests with premium amenities and personalized service.",
    image: require("../assets/bell429.jpeg"),
    icon: Crown,
    link: "/services/vip-charter",
  },
  {
    id: "corporate-charter",
    name: "Corporate Charter",
    description:
      "Business helicopter services for corporate executives. Save time and boost productivity.",
    image: require("../assets/Legacy-650-ext-JS.jpg"),
    icon: Briefcase,
    link: "/services/corporate-charter",
  },
  {
    id: "election-flying",
    name: "Election Flying",
    description:
      "Campaign aviation support to cover multiple rallies and reach more voters efficiently.",
    image: require("../assets/Airbus-H160-election_flying.jpg"),
    icon: Vote,
    link: "/services/election-flying",
  },
  {
    id: "ems-organ-transport",
    name: "Emergency Medical Services",
    description:
      "Life-saving emergency medical services for rapid organ transport when every second counts.",
    image: require("../assets/ems_main.png"),
    icon: Heart,
    link: "/services/ems-organ-transport",
  },
  {
    id: "flower-drop",
    name: "Flower Drop",
    description:
      "Aerial flower showers for temples, statue inaugurations, and special occasions.",
    image: require("../assets/flowerdrop_main.jpeg"),
    icon: Flower2,
    link: "/services/flower-drop",
  },
  {
    id: "wedding-entry",
    name: "Wedding & Film Photoshoot",
    description:
      "Make grand wedding entrances and capture stunning aerial photographs.",
    image: require("../assets/wedding_highkraft.jpeg"),
    icon: HeartHandshake,
    link: "/services/wedding-entry",
  },
  {
    id: "temple-heritage",
    name: "Temple & Heritage Visits",
    description:
      "Comfortable pilgrimage charters to temples and heritage sites across India.",
    image: "https://images.unsplash.com/photo-1559770968-53924e9b32de",
    icon: Landmark,
    link: "/services/temple-heritage",
  },
  {
    id: "joy-rides",
    name: "Joy Rides",
    description:
      "Thrilling scenic helicopter rides over Bangalore for unforgettable experiences.",
    image: "https://images.pexels.com/photos/1686990/pexels-photo-1686990.jpeg",
    icon: Sparkles,
    link: "/services/joy-rides",
  },
];

// Hero carousel images
const heroImages = [
  {
    url: require("../assets/bst_bg_highkraft_latest.jpeg"),
    alt: "HighKraft Aviation Helicopter Flight",
  },
  {
    url: require("../assets/bst_another_.jpeg"),
    alt: "VIP Helicopter Charter",
  },
  {
    url: require("../assets/udt_highkrtaft_background_vt-udy_enhanced_pics.jpg"),
    alt: "Helicopter Joy Ride Experience",
  },
  {
    url: require("../assets/Hawker-900XP.jpg"),
    alt: "Luxury Helicopter Service",
  },
  {
    url: require("../assets/Beechcraft-King-Air-360.jpeg"),
    alt: "Corporate Helicopter Charter",
  },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Hero carousel auto-rotation
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Fetch upcoming events on component mount
    const fetchUpcomingEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        if (response.data.success) {
          setUpcomingEvents(response.data.events);
        }
      } catch (error) {
        console.error("Error fetching upcoming events:", error);
      }
    };

    fetchUpcomingEvents();
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await axios.post(`${API}/enquiries`, formData);

      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({ name: "", email: "", phone: "", message: "" });
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        "Failed to send message. Please try again.";
      toast.error(errorMessage);
      console.error("Error submitting form:", error);
    }
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      "Hi, I'm interested in helicopter charter services. Please provide more details.",
    );
    window.open(`https://wa.me/919148010687?text=${message}`, "_blank");
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <nav className="container mx-auto px-4 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_dda025fc-cd40-4797-8c5d-d84f34dad8eb/artifacts/i3r7o5v7_HighKraft.jpeg"
              alt="HighKraft Aviation Logo"
              className="w-14 h-14 md:w-20 md:h-20 object-contain"
            />
            <div className="flex flex-col">
              <h1 className="text-lg md:text-2xl lg:text-3xl font-bold text-aviation-blue tracking-tight leading-none">
                HighKraft Aviation
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <div className="h-0.5 w-6 md:w-8 bg-aviation-gold"></div>
                <span className="text-xs font-medium text-aviation-gold uppercase tracking-wider">
                  Krafting Flight Solutions
                </span>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <button
              onClick={() => scrollToSection("home")}
              className="nav-link"
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection("about")}
              className="nav-link"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection("services")}
              className="nav-link"
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection("fleet")}
              className="nav-link"
            >
              Our Fleet
            </button>
            <button
              onClick={() => scrollToSection("joy-rides")}
              className="nav-link"
            >
              Joy Rides
            </button>
            <button
              onClick={() => scrollToSection("safety")}
              className="nav-link"
            >
              Safety
            </button>
            {/* <button
              onClick={() => scrollToSection("gallery")}
              className="nav-link"
            >
              Gallery
            </button> */}
            <button onClick={() => scrollToSection("faq")} className="nav-link">
              FAQs
            </button>
            <button
              onClick={() => scrollToSection("contact")}
              className="nav-link"
            >
              Contact
            </button>
            <Button
              onClick={handleWhatsAppClick}
              className="bg-aviation-gold hover:bg-aviation-gold-dark text-white"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </nav>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 py-4">
            <div className="container mx-auto px-4 flex flex-col gap-3">
              <button
                onClick={() => scrollToSection("home")}
                className="text-left py-2 hover:text-aviation-blue transition-colors"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection("about")}
                className="text-left py-2 hover:text-aviation-blue transition-colors"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection("services")}
                className="text-left py-2 hover:text-aviation-blue transition-colors"
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection("fleet")}
                className="text-left py-2 hover:text-aviation-blue transition-colors"
              >
                Our Fleet
              </button>
              <button
                onClick={() => scrollToSection("joy-rides")}
                className="text-left py-2 hover:text-aviation-blue transition-colors"
              >
                Joy Rides
              </button>
              <button
                onClick={() => scrollToSection("safety")}
                className="text-left py-2 hover:text-aviation-blue transition-colors"
              >
                Safety
              </button>
              <button
                onClick={() => scrollToSection("gallery")}
                className="text-left py-2 hover:text-aviation-blue transition-colors"
              >
                Gallery
              </button>
              <button
                onClick={() => scrollToSection("faq")}
                className="text-left py-2 hover:text-aviation-blue transition-colors"
              >
                FAQs
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                className="text-left py-2 hover:text-aviation-blue transition-colors"
              >
                Contact
              </button>
              <Button
                onClick={handleWhatsAppClick}
                className="bg-aviation-gold hover:bg-aviation-gold-dark text-white w-full"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section with Carousel */}
      <section id="home" className="pt-24 md:pt-20 relative">
        <div className="relative h-auto md:h-[700px] overflow-hidden">
          {/* Carousel Images */}
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${
                index === currentSlide ? "opacity-100" : "opacity-0"
              }`}
              style={{ backgroundImage: `url(${image.url})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-aviation-blue/85 to-aviation-blue/75"></div>
            </div>
          ))}

          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="max-w-3xl text-white pt-6 pb-12 md:py-0">
              <div
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4 md:mb-6"
                style={{ textShadow: "1px 1px 4px rgba(0,0,0,0.5)" }}
              >
                <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-aviation-gold" />
                <span className="text-xs md:text-sm font-medium">
                  Premium Aviation Experience
                </span>
              </div>

              <h1
                className="text-2xl sm:text-3xl md:text-6xl font-bold mb-4 md:mb-6 leading-snug md:leading-tight"
                style={{ textShadow: "2px 2px 8px rgba(0,0,0,0.5)" }}
              >
                HighKraft Aviation — Elevating Luxury Air Travel
              </h1>
              {upcomingEvents.length > 0 && (
                <div
                  onClick={() => scrollToSection("joy-rides")}
                  className="
      inline-flex items-center gap-2
      px-4 py-1.5
      mb-4
      rounded-full
      border border-aviation-gold
      bg-aviation-gold/15
      text-aviation-gold
      text-sm font-semibold
      cursor-pointer
      hover:bg-aviation-gold hover:text-white
      transition-all duration-200
      w-fit
    "
                  style={{
                    textShadow: "1px 1px 4px rgba(0,0,0,0.4)",
                    animation: "pulse 5s infinite",
                  }}
                >
                  🟠 Joy Ride Live
                </div>
              )}

              <p
                className="text-sm sm:text-base md:text-2xl mb-3 md:mb-4 text-white/95"
                style={{ textShadow: "1px 1px 6px rgba(0,0,0,0.5)" }}
              >
                Premium helicopter and aircraft charters for business, leisure,
                medical, pilgrimage, and VIP travel across India.
              </p>

              <p
                className="block text-xs sm:text-sm md:text-lg mb-4 sm:mb-6 md:mb-8 text-white/90 leading-relaxed"
                style={{ textShadow: "1px 1px 4px rgba(0,0,0,0.5)" }}
              >
                From executive corporate movements to wedding entries, election
                flying, aerial photography, and scenic joy rides — we deliver
                safe, comfortable, and unforgettable flying experiences with
                world-class aircraft and expert crew.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <Button
                  onClick={() => scrollToSection("contact")}
                  size="lg"
                  className="bg-aviation-gold hover:bg-aviation-gold-dark text-white text-sm md:text-lg px-6 md:px-8 py-4 md:py-6 shadow-lg"
                >
                  <Navigation className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                  Book Your Ride
                </Button>
                <Button
                  onClick={handleWhatsAppClick}
                  size="lg"
                  variant="outline"
                  className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-aviation-blue text-sm md:text-lg px-6 md:px-8 py-4 md:py-6 shadow-lg"
                >
                  <MessageCircle className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                  WhatsApp Enquiry
                </Button>
              </div>

              <div
                className="mt-6 flex flex-col sm:flex-row gap-3 sm:gap-6 text-xs sm:text-sm"
                style={{ textShadow: "1px 1px 4px rgba(0,0,0,0.5)" }}
              >
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-aviation-gold" />
                  <span>Jakkur Aerodrome, Bangalore</span>
                </div>

                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-aviation-gold" />
                  <a href="tel:9148010687" className="hover:underline">
                    9148010687
                  </a>
                  <span>/</span>
                  <a href="tel:9900027530" className="hover:underline">
                    9900027530
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Carousel Indicators */}
          <div className="absolute bottom-4 md:bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? "bg-aviation-gold w-8"
                    : "bg-white/50 hover:bg-white/80"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={() =>
              setCurrentSlide(
                (prev) => (prev - 1 + heroImages.length) % heroImages.length,
              )
            }
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/30 flex items-center justify-center text-white hover:bg-white/20 transition-all hidden md:flex"
            aria-label="Previous slide"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <button
            onClick={() =>
              setCurrentSlide((prev) => (prev + 1) % heroImages.length)
            }
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/30 flex items-center justify-center text-white hover:bg-white/20 transition-all hidden md:flex"
            aria-label="Next slide"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </section>

      {/* Features Bar */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {mockData.features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex-shrink-0">
                  {feature.icon === "shield" && (
                    <Shield className="w-10 h-10 text-aviation-gold" />
                  )}
                  {feature.icon === "award" && (
                    <Award className="w-10 h-10 text-aviation-gold" />
                  )}
                  {feature.icon === "users" && (
                    <Users className="w-10 h-10 text-aviation-gold" />
                  )}
                  {feature.icon === "clock" && (
                    <Clock className="w-10 h-10 text-aviation-gold" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-aviation-blue">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-aviation-blue/70">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Us */}
      <section id="about" className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-aviation-blue mb-6">
                About HighKraft Aviation
              </h2>
              <p className="text-lg text-aviation-blue/80 mb-4">
                HighKraft Aviation Services Pvt. Ltd. delivers safe, efficient,
                and reliable luxury air transportation across Bangalore and
                beyond. We are committed to redefining private travel through
                exceptional service, modern aircraft, and uncompromising safety
                standards.
              </p>
              <p className="text-lg text-aviation-blue/80 mb-6">
                With a sophisticated fleet of helicopters and private jets,
                highly trained pilots, and a dedicated professional team, we
                ensure every journey is seamless, comfortable, and memorable.
                Our customer-first approach, rigorous operational discipline,
                and passion for aviation excellence make HighKraft Aviation the
                preferred partner for discerning travelers, corporate clients,
                and premium charter services.
              </p>
              <p className="text-lg text-aviation-blue/80 mb-6">
                Experience the freedom of flying with confidence, comfort, and
                class.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-aviation-blue/5 p-4 rounded-lg border-l-4 border-aviation-gold">
                  <div className="text-3xl font-bold text-aviation-blue mb-1">
                    500+
                  </div>
                  <div className="text-sm text-aviation-blue/70">
                    Happy Customers
                  </div>
                </div>
                <div className="bg-aviation-blue/5 p-4 rounded-lg border-l-4 border-aviation-gold">
                  <div className="text-3xl font-bold text-aviation-blue mb-1">
                    20+
                  </div>
                  <div className="text-sm text-aviation-blue/70">
                    Years Experience
                  </div>
                </div>
                <div className="bg-aviation-blue/5 p-4 rounded-lg border-l-4 border-aviation-gold">
                  <div className="text-3xl font-bold text-aviation-blue mb-1">
                    500+ Hours
                  </div>
                  <div className="text-sm text-aviation-blue/70">
                    Safe Flights
                  </div>
                </div>
                <div className="bg-aviation-blue/5 p-4 rounded-lg border-l-4 border-aviation-gold">
                  <div className="text-3xl font-bold text-aviation-blue mb-1">
                    24/7
                  </div>
                  <div className="text-sm text-aviation-blue/70">
                    Support Available
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src={require("../assets/bst_bg_highkraft_latest.jpeg")}
                alt="Helicopter"
                className="rounded-2xl shadow-2xl w-full h-[260px] sm:h-[320px] md:h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-aviation-gold text-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center gap-3">
                  <HeartHandshake className="w-8 h-8" />
                  <div>
                    <div className="text-2xl font-bold">Trusted</div>
                    <div className="text-sm">Aviation Partner</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-aviation-gold/10 border border-aviation-gold px-4 py-2 rounded-full mb-4">
              <Plane className="w-5 h-5 text-aviation-gold" />
              <span className="text-sm font-semibold text-aviation-gold">
                Helicopter Charter Services
              </span>
            </div>
            <h2 className="text-4xl font-bold text-aviation-blue mb-4">
              Our Services
            </h2>
            <p className="text-lg text-aviation-blue/70 max-w-2xl mx-auto">
              From luxury VIP charters to life-saving medical transport,
              discover our comprehensive range of helicopter services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {servicesData.map((service) => {
              const IconComponent = service.icon;
              return (
                <Link key={service.id} to={service.link} className="group">
                  <Card className="h-full overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-aviation-gold">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.name}
                        style={{ objectFit: service.heroImageFit || "cover" }}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-4">
                        <div className="w-10 h-10 bg-aviation-gold rounded-full flex items-center justify-center">
                          <IconComponent className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-5">
                      <h3 className="text-xl font-bold text-aviation-blue mb-2 group-hover:text-aviation-gold transition-colors">
                        {service.name}
                      </h3>
                      <p className="text-sm text-aviation-blue/70 mb-4 line-clamp-2">
                        {service.description}
                      </p>
                      <div className="flex items-center text-aviation-gold font-medium text-sm">
                        Learn More
                        <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Fleet Section */}
      <section id="fleet" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-aviation-gold/10 border border-aviation-gold px-4 py-2 rounded-full mb-4">
              <Plane className="w-5 h-5 text-aviation-gold" />
              <span className="text-sm font-semibold text-aviation-gold">
                Our Modern Fleet
              </span>
            </div>
            <h2 className="text-4xl font-bold text-aviation-blue mb-4">
              Our Helicopters & Aircraft
            </h2>
            <p className="text-lg text-aviation-blue/70 max-w-2xl mx-auto">
              Experience luxury and safety with our state-of-the-art helicopter
              fleet, maintained to the highest standards
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockData.fleet.map((aircraft, index) => (
              <Card
                key={index}
                className="overflow-hidden flex flex-col h-full shadow-sm hover:shadow-xl transition-all duration-300"
              >
                {/* Image */}
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={aircraft.image}
                    alt={aircraft.name}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>

                {/* Content */}
                <CardContent className="p-4 flex flex-col flex-grow text-center">
                  <h3 className="text-lg font-bold text-aviation-blue mb-2">
                    {aircraft.name}
                  </h3>

                  {/* short description — trimmed */}
                  <p className="text-sm text-aviation-blue/70 line-clamp-3 mb-3">
                    {aircraft.description}
                  </p>

                  <div className="mt-auto space-y-2 text-left">
                    <div className="flex items-center gap-2 text-xs">
                      <Users className="w-4 h-4 text-aviation-gold" />
                      <span>
                        <strong>Capacity:</strong> {aircraft.capacity}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-xs">
                      <Clock className="w-4 h-4 text-aviation-gold" />
                      <span>
                        <strong>Range:</strong> {aircraft.range}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-xs">
                      <Shield className="w-4 h-4 text-aviation-gold" />
                      <span className="line-clamp-2">
                        <strong>Features:</strong> {aircraft.features}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Helicopter Joy Rides */}
      <section id="joy-rides" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-aviation-blue mb-4">
              Helicopter Joy Rides
            </h2>
            <p className="text-lg text-aviation-blue/70 max-w-2xl mx-auto">
              Soar above Bangalore and witness the city's beauty from a
              breathtaking aerial perspective. An unforgettable experience
              awaits!
            </p>
          </div>

          {/* Upcoming Events Section */}
          {upcomingEvents.length > 0 && (
            <div className="mb-16">
              <div className="flex items-center justify-center gap-2 mb-8">
                <Calendar className="w-6 h-6 text-aviation-gold" />
                <h3 className="text-3xl font-bold text-aviation-blue">
                  Upcoming Planned Events
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                {upcomingEvents.map((event, index) => (
                  <Card
                    key={index}
                    className="border-l-4 border-aviation-gold hover:shadow-xl transition-all"
                  >
                    <CardHeader className="bg-aviation-blue/5">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-aviation-blue text-xl mb-2">
                            {event.eventName}
                          </CardTitle>
                          <div className="flex items-center gap-2 text-sm text-aviation-blue/70">
                            <Calendar className="w-4 h-4 text-aviation-gold" />
                            <span>
                              {new Date(
                                event.eventDate + "T00:00:00",
                              ).toLocaleDateString("en-US", {
                                weekday: "long",
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })}{" "}
                              at {event.startTime} - {event.endTime}
                            </span>
                          </div>
                        </div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-700 border border-green-200">
                          <span className="text-green-600">✅</span>
                          Slots Available
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <p className="text-aviation-blue/70 mb-4">
                        {event.description}
                      </p>

                      <div className="space-y-3 mb-4">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-5 h-5 text-aviation-gold mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="font-semibold text-aviation-blue text-sm">
                              Location:
                            </span>
                            <p className="text-aviation-blue/70 text-sm">
                              {event.location || event.locationAddress}
                            </p>
                          </div>
                        </div>

                        {(event.price || event.pricePerSeat) && (
                          <div className="flex items-start gap-2">
                            <Info className="w-5 h-5 text-aviation-gold mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="font-semibold text-aviation-blue text-sm">
                                Price(Inclusive of taxes):
                              </span>
                              <p className="text-aviation-blue/70 text-sm">
                                {event.price ||
                                  `₹${event.pricePerSeat?.toLocaleString()} per seat`}
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="flex items-start gap-2">
                          <Phone className="w-5 h-5 text-aviation-gold mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="font-semibold text-aviation-blue text-sm">
                              Booking Info:
                            </span>
                            <p className="text-aviation-blue/70 text-sm">
                              {event.bookingInfo ||
                                "Book online or contact us via WhatsApp"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <Button
                          onClick={() =>
                            (window.location.href =
                              event.bookingUrl || "/app/book-joyride")
                          }
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Ticket className="w-4 h-4 mr-2" />
                          Book Online
                        </Button>
                        <Button
                          onClick={handleWhatsAppClick}
                          className="flex-1 bg-aviation-gold hover:bg-aviation-gold-dark text-white"
                        >
                          <MessageCircle className="w-4 h-4 mr-2" />
                          WhatsApp
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.joyRideFeatures.map((feature, index) => (
              <Card
                key={index}
                className="hover:shadow-xl transition-all hover:-translate-y-1"
              >
                <CardHeader>
                  <div className="w-12 h-12 bg-aviation-gold/10 rounded-full flex items-center justify-center mb-4 border-2 border-aviation-gold">
                    {feature.icon === "plane" && (
                      <Plane className="w-6 h-6 text-aviation-gold" />
                    )}
                    {feature.icon === "navigation" && (
                      <Navigation className="w-6 h-6 text-aviation-gold" />
                    )}
                    {feature.icon === "sparkles" && (
                      <Sparkles className="w-6 h-6 text-aviation-gold" />
                    )}
                    {feature.icon === "users" && (
                      <Users className="w-6 h-6 text-aviation-gold" />
                    )}
                    {feature.icon === "clock" && (
                      <Clock className="w-6 h-6 text-aviation-gold" />
                    )}
                    {feature.icon === "heart" && (
                      <HeartHandshake className="w-6 h-6 text-aviation-gold" />
                    )}
                  </div>
                  <CardTitle className="text-aviation-blue">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-aviation-blue/70">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Safety & Certifications */}
      <section id="safety" className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-aviation-gold/10 border border-aviation-gold px-4 py-2 rounded-full mb-4">
              <Shield className="w-5 h-5 text-aviation-gold" />
              <span className="text-sm font-semibold text-aviation-gold">
                Your Safety is Our Priority
              </span>
            </div>
            <h2 className="text-4xl font-bold text-aviation-blue mb-4">
              Safety & Certifications
            </h2>
            <p className="text-lg text-aviation-blue/70 max-w-2xl mx-auto">
              We maintain the highest safety standards with certified pilots,
              regular maintenance, and strict operational protocols.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockData.safetyCertifications.map((cert, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow border-l-4 border-aviation-gold"
              >
                <CheckCircle2 className="w-8 h-8 text-aviation-gold mb-4" />
                <h3 className="text-xl font-semibold text-aviation-blue mb-2">
                  {cert.title}
                </h3>
                <p className="text-aviation-blue/70">{cert.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      {/* <section id="gallery" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-aviation-blue mb-4">
              Experience Gallery
            </h2>
            <p className="text-lg text-aviation-blue/70">
              Glimpses of the breathtaking views and unforgettable moments
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {mockData.galleryImages.map((image, index) => (
              <div
                key={index}
                className="relative overflow-hidden rounded-xl group cursor-pointer h-64"
              >
                <img
                  src={image}
                  alt={`Gallery ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Pricing */}
      <section id="pricing" className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-aviation-blue mb-6">
              Pricing & Packages
            </h2>
            <p className="text-lg text-aviation-blue/70 mb-8">
              We offer customized packages tailored to your preferences and
              group size. Get in touch with us for detailed pricing information.
            </p>

            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <div className="w-20 h-20 bg-aviation-gold/10 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-aviation-gold">
                <Phone className="w-10 h-10 text-aviation-gold" />
              </div>
              <h3 className="text-2xl font-bold text-aviation-blue mb-4">
                Contact Us for Pricing
              </h3>
              <p className="text-aviation-blue/70 mb-8">
                Our team will help you choose the perfect package based on your
                requirements, duration, and special requests.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={handleWhatsAppClick}
                  size="lg"
                  className="bg-aviation-gold hover:bg-aviation-gold-dark text-white"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp: 9148010687
                </Button>
                <Button
                  onClick={() => scrollToSection("contact")}
                  size="lg"
                  variant="outline"
                  className="border-2 border-aviation-gold text-aviation-gold hover:bg-aviation-gold hover:text-white"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Send Enquiry
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-aviation-blue mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg text-aviation-blue/70">
              Real experiences from our happy customers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mockData.testimonials.map((testimonial, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-5 h-5 fill-aviation-gold text-aviation-gold"
                      />
                    ))}
                  </div>
                  <p className="text-aviation-blue/70 mb-6 italic">
                    "{testimonial.comment}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-aviation-blue/10 rounded-full flex items-center justify-center">
                      <span className="text-aviation-blue font-semibold text-lg">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <div className="font-semibold text-aviation-blue">
                        {testimonial.name}
                      </div>
                      <div className="text-sm text-aviation-blue/70">
                        {testimonial.location}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section id="faq" className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-aviation-blue mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-aviation-blue/70">
                Find answers to common questions about our charter services
              </p>
            </div>

            <Accordion type="single1" collapsible className="space-y-4">
              {mockData.faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 px-6"
                >
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-aviation-blue">
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-aviation-blue/70">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-aviation-blue mb-4">
              Get in Touch
            </h2>
            <p className="text-lg text-aviation-blue/70">
              Ready to book your charter services? Contact us today!
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-aviation-blue">
                    Send us a message
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Input
                        type="text"
                        name="name"
                        placeholder="Your Name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <Input
                        type="email"
                        name="email"
                        placeholder="Your Email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <Input
                        type="tel"
                        name="phone"
                        placeholder="Your Phone Number"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <Textarea
                        name="message"
                        placeholder="Your Message"
                        rows={5}
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-aviation-blue hover:bg-aviation-blue-dark text-white"
                    >
                      Send Message
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <div className="mt-6 space-y-4">
                <div className="flex items-center gap-4 bg-white p-4 rounded-lg shadow-sm border-l-4 border-aviation-gold">
                  <MapPin className="w-6 h-6 text-aviation-gold" />
                  <div>
                    <div className="font-semibold text-aviation-blue">
                      Location
                    </div>
                    <div className="text-aviation-blue/70">
                      Jakkur Aerodrome, Bangalore
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-white p-4 rounded-lg shadow-sm border-l-4 border-aviation-gold">
                  <Phone className="w-6 h-6 text-aviation-gold" />
                  <div>
                    <div className="font-semibold text-aviation-blue">
                      Phone
                    </div>
                    <a href="tel:9148010687" className="text-aviation-blue">
                      9148010687
                    </a>
                    <span className="mx-2">|</span>
                    <a href="tel:9900027530" className="text-aviation-blue">
                      9900027530
                    </a>
                  </div>
                </div>
                <Button
                  onClick={handleWhatsAppClick}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  size="lg"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Chat on WhatsApp
                </Button>
              </div>
            </div>
            {/* Map */}
            <div>
              <div className="bg-white rounded-xl shadow-lg overflow-hidden h-full min-h-[500px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3885.8936481936834!2d77.59738731482204!3d13.081058990766836!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae173f6b10c1f3%3A0x88d3bed5c0a53a06!2sJakkur%20Aerodrome!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: "500px" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Jakkur Aerodrome Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="hangar" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-aviation-blue mb-4">
              Hangar & Operations Base
            </h2>
            <p className="text-lg text-aviation-blue/70">
              Where safety, maintenance, and precision meet aviation excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src={hangar1}
                className="w-full h-80 object-cover"
                alt="Hangar 1"
              />
            </div>

            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src={hangar2}
                className="w-full h-80 object-cover"
                alt="Hangar 2"
              />
            </div>

            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src={hangar3}
                className="w-full h-80 object-cover"
                alt="Hangar 3"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* ------------------------ NEW TEAM SECTION ------------------------ */}
      {/* ------------------------------------------------------------------ */}

      <section id="team" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-aviation-blue mb-4">
              Our Team
            </h2>
            <p className="text-lg text-aviation-blue/70 max-w-2xl mx-auto">
              Aviation professionals delivering safety, reliability, and
              exceptional service.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h3 className="text-2xl font-semibold text-aviation-blue mb-4">
                Experience You Can Trust
              </h3>

              <p className="text-aviation-blue/80 mb-4">
                Our crew includes DGCA certified pilots, licensed AMEs, and
                experienced operations specialists dedicated to your comfort and
                safety.
              </p>

              <ul className="space-y-3 text-aviation-blue">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="text-aviation-gold" /> Certified
                  Commercial Pilots
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="text-aviation-gold" /> Aviation
                  Engineers & Technicians
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="text-aviation-gold" /> Ground
                  Handling Experts
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="text-aviation-gold" /> 24x7 Customer
                  Support Team
                </li>
              </ul>
            </div>

            <div className="rounded-2xl shadow-2xl overflow-hidden">
              <img
                src={teamPhoto}
                alt="HighKraft Team"
                className="
      w-full
      h-auto
      sm:h-[300px]
      md:h-[380px]
      lg:h-[420px]
      object-contain
      sm:object-cover
      bg-white
    "
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-aviation-blue text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-1 flex flex-col items-start">
              <img
                src="https://customer-assets.emergentagent.com/job_dda025fc-cd40-4797-8c5d-d84f34dad8eb/artifacts/i3r7o5v7_HighKraft.jpeg"
                alt="HighKraft Aviation Logo"
                className="w-40 h-40 object-contain mb-4"
              />
              <p className="text-white/80 text-sm">
                Your trusted partner for safe and memorable helicopter charter,
                management, and joy ride experiences.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-white/80">
                <li>
                  <button
                    onClick={() => scrollToSection("home")}
                    className="hover:text-white transition-colors"
                  >
                    Home
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection("about")}
                    className="hover:text-white transition-colors"
                  >
                    About Us
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection("joy-rides")}
                    className="hover:text-white transition-colors"
                  >
                    Joy Rides
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection("safety")}
                    className="hover:text-white transition-colors"
                  >
                    Safety
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-white/80">
                <li>
                  <Link
                    to="/services/vip-charter"
                    className="hover:text-white transition-colors"
                  >
                    VIP Charter
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/corporate-charter"
                    className="hover:text-white transition-colors"
                  >
                    Corporate Charter
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/wedding-entry"
                    className="hover:text-white transition-colors"
                  >
                    Wedding Entry
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/joy-rides"
                    className="hover:text-white transition-colors"
                  >
                    Joy Rides
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-2 text-sm text-white/80">
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Jakkur Aerodrome, Bangalore</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <a href="tel:9148010687" className="hover:text-white">
                    9148010687
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-white/80">
                © 2025 HighKraft Aviation Services Pvt Limited. All rights
                reserved.
              </p>
              <div className="flex gap-4">
                <span className="text-sm text-white/80">Follow us:</span>
                <div className="flex gap-3">
                  <a
                    href="#"
                    className="hover:text-aviation-gold transition-colors"
                  >
                    Facebook
                  </a>
                  <a
                    href="#"
                    className="hover:text-aviation-gold transition-colors"
                  >
                    Instagram
                  </a>
                  <a
                    href="#"
                    className="hover:text-aviation-gold transition-colors"
                  >
                    Twitter
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
